<template>
  <div class="developer-page">
    <page-header
      title="Developer Profile"
    >
      <template #action>
        <v-btn color="button" @click="goBack">
          BACK
        </v-btn>
      </template>
      <template #breadcrumbs>
        <router-link :to="{ name: 'projects' }">
          Projects
        </router-link>
        <span>
          / {{ developer.personal_data.first_name }} {{ developer.personal_data.last_name }}
        </span>
      </template>
    </page-header>

    <PersonalInformationsClientCard :personal-data="developer.personal_data" />

    <section class="developer-data">
      <v-row>
        <v-col cols="12" lg="6">
          <FeaturesCard
            v-if="isNotEmpty(featureGroups[0])"
            :featureGroups="featureGroups[0]"
            primary
            readonly
          />

          <FeaturesCard
            v-if="isNotEmpty(featureGroups[1])"
            :featureGroups="featureGroups[1]"
            readonly
          />

          <AttachmentsCard
            v-if="developer.attachments && developer.attachments.length"
            :attachments="developer.attachments"
            readonly
          />
        </v-col>

        <v-col cols="12" lg="6">
          <PersonalityCard
            :skills="developer.soft_skills"
            :token="developer.membership_token"
            readonly
          />

          <FeaturesCard
            v-if="isNotEmpty(featureGroups[2])"
            :featureGroups="featureGroups[2]"
            readonly
          />

          <EducationCard
            v-if="developer.education && developer.education.length"
            :education="developer.education"
            readonly
          />

          <DescriptionCard
            v-if="developer.description"
            :description="developer.description"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col class="text-right pt-0">
          <v-btn
            color="blue"
            large
            class="developer-data__cv-btn"
            :loading="isCvProcessing"
            @click="generateCV"
          >
            DOWNLOAD CV
          </v-btn>
        </v-col>
      </v-row>
    </section>

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import PageHeader from '@/components/PageHeader.vue';
import DescriptionCard from '@/components/Developers/Show/DescriptionCard.vue';
import FeaturesCard from '@/components/Developers/Show/FeaturesCard.vue';
import PersonalityCard from '@/components/Developers/Show/PersonalityCard.vue';
import EducationCard from '@/components/Developers/Show/EducationCard.vue';
import PersonalInformationsClientCard from '@/components/Developers/Show/PersonalInformationsClientCard.vue';
import AttachmentsCard from '@/components/AttachmentsCard.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';

export default {
  metaInfo() {
    return {
      title: `Developer - ${this.developer.personal_data.last_name} ${this.developer.personal_data.first_name}`,
    };
  },

  components: {
    PageHeader,
    PersonalInformationsClientCard,
    FeaturesCard,
    PersonalityCard,
    EducationCard,
    DescriptionCard,
    AttachmentsCard,
    ConfirmDialog,
  },

  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      isCvProcessing: false,
    };
  },

  computed: {
    ...mapState('developer', [
      'developer',
    ]),

    ...mapState('features', [
      'featuresData',
    ]),

    featureGroups() {
      return [
        [
          {
            name: 'Primary Roles',
            slug: 'roles',
            data: this.developer.roles,
            primary: true,
          },
          {
            name: 'Top Skills',
            slug: 'top_skills',
            data: this.developer.top_skills,
            primary: true,
          },
          {
            name: 'Skills',
            slug: 'skills',
            data: this.developer.skills,
            primary: true,
          },
          {
            name: 'Languages',
            slug: 'languages',
            data: this.developer.languages,
            primary: true,
          },
        ],
        [
          {
            name: 'Tools experience',
            slug: 'tools',
            data: this.developer.tools,
          },
          {
            name: 'Management methodologies experience',
            slug: 'management',
            data: this.developer.management,
          },
        ],
        [
          {
            name: 'Technology preferences',
            slug: 'technologies',
            data: this.developer.technologies,
          },
          {
            name: 'Project areas preferences',
            slug: 'project_areas',
            data: this.developer.project_areas,
          },
          {
            name: 'Hobbies',
            slug: 'hobbies',
            data: this.developer.hobbies,
          },
        ],
      ];
    },
  },

  mounted() {
    this.fetchDeveloper(this.id);
  },

  methods: {
    ...mapActions('developer', [
      'fetchDeveloper',
      'generateDeveloperCv',
    ]),

    goBack() {
      this.$router.push({ name: 'hr-developers' });
    },

    generateCV() {
      this.isCvProcessing = true;

      this.generateDeveloperCv(this.id)
        .then((resp) => {
          const fileName = `${this.developer.personal_data.last_name}-${this.developer.personal_data.first_name}`;
          const downloadLink = document.createElement('a');
          downloadLink.target = '_blank';
          downloadLink.download = `cv-${fileName}.pdf`;

          const blob = new Blob([resp.data], { type: 'application/pdf' });
          const URL = window.URL || window.webkitURL;
          const downloadUrl = URL.createObjectURL(blob);
          downloadLink.href = downloadUrl;
          document.body.append(downloadLink);
          downloadLink.click();

          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(downloadUrl);

          this.$emit('success');
        })
        .finally(() => {
          this.isCvProcessing = false;
        });
    },

    isNotEmpty(groups) {
      return groups.filter((group) => group.data.length > 0).length;
    },
  },
};
</script>

<style lang="scss" scoped>
.developer-data {
  margin-bottom: 0px;

  &__delete-btn {
    min-width: 27% !important;
    color: white;
    margin-right: 24px;
  }

  &__cv-btn {
    min-width: 27% !important;
    color: white;
  }
}
</style>
